import React from 'react';
import {AuthService} from '../api';
import type {ReauthProvider} from '../api/types';
import {
  ReauthFailure,
  classifyReauthError,
  isStaleModeError,
} from '../utils/SocialReauth';
import useApiClient from './useApiClient';

/**
 * How long a challenge lives on the server (`reauth.CHALLENGE_TTL`, 300 s),
 * less a margin for the request that drew it. Timed from the reply's
 * arrival on the local clock, never against `expires_at`: that is the
 * server's epoch, and a client clock a few minutes fast would read every
 * challenge as already expired and redraw it forever.
 */
export const CHALLENGE_LIFETIME_MS = 290_000;

/** One live re-authentication challenge (POST auth/reauth/begin/). */
export type ReauthChallenge = {
  provider: ReauthProvider;
  /** The RAW server nonce. Apple wants its SHA-256 (hex), Google it as is. */
  nonce: string;
  /** The linked account's address, to preselect it in the provider's picker. */
  loginHint: string | null;
  /** When it stops being usable, LOCAL clock (ms): see CHALLENGE_LIFETIME_MS. */
  expiresAtMs: number;
};

/**
 * The platform's provider sign-in, injected by each app: the fresh ID token,
 * or `null` when the user dismissed the provider's sheet (which shows
 * nothing). A throw is a failure.
 */
export type ObtainIdToken = (
  provider: ReauthProvider,
  challenge: ReauthChallenge,
) => Promise<string | null>;

type SocialReauthState =
  /** The confirm step. */
  | {status: 'idle'}
  | {status: 'busy'; provider: ReauthProvider}
  /** Confirmed: the single-use proof the email change sends. */
  | {status: 'confirmed'; provider: ReauthProvider; reauthToken: string}
  | {status: 'failed'; failure: ReauthFailure}
  /** The email change refused the proof (expired, spent): confirm again. */
  | {status: 'expired'};

type Options = {
  /** Required by `confirm`; the webapp's Google button uses `complete`. */
  obtainIdToken?: ObtainIdToken;
  /**
   * Called when begin says /me is out of date (`isStaleModeError`): a
   * password was set, the provider unlinked, or the flow switched off. The
   * app refreshes the user, and the screen moves to the right flow.
   */
  onStaleMode?: () => void;
};

const isLive = (challenge: ReauthChallenge | null) =>
  challenge !== null && challenge.expiresAtMs > Date.now();

/**
 * Confirm a password-less user with their linked Google or Apple account
 * before an email change (backend#263 Phase B): begin draws a nonce, the
 * injected provider step signs in again, complete trades the ID token for a
 * single-use `reauth_token`. All requests are silent: the app renders the
 * outcome from `state`.
 *
 * Begin and complete share 10 attempts an hour, and the server keeps one
 * challenge per user, spent only by complete. So the challenge is kept
 * (`prepared`) until complete spends it or it runs out: a dismissed sheet or
 * a failed provider step leaves it usable, and the next press for that
 * provider uses it with nothing awaited before the provider step (a browser
 * popup then opens inside the gesture). Nothing is drawn on mount;
 * `ensurePrepared` draws one lazily when the app sees the user coming.
 */
const useSocialReauth = ({obtainIdToken, onStaleMode}: Options) => {
  const {fetch} = useApiClient();
  const [state, setState] = React.useState<SocialReauthState>({
    status: 'idle',
  });
  const [prepared, setPrepared] = React.useState<ReauthChallenge | null>(null);
  // Mirrors `prepared` for the handlers, which may run before a re-render.
  const preparedRef = React.useRef<ReauthChallenge | null>(null);
  // A silent ensurePrepared failed: not again until the user does something.
  const silentFailed = React.useRef(false);
  const inFlight = React.useRef(false);
  // The silent ensurePrepared draw, while it runs: a press that lands before
  // it answers waits for it instead of being dropped by `inFlight`.
  const silentDraw = React.useRef<Promise<void> | null>(null);
  const mounted = React.useRef(true);
  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const setStateSafe = (value: SocialReauthState) => {
    if (mounted.current) setState(value);
  };
  const keep = (challenge: ReauthChallenge | null) => {
    preparedRef.current = challenge;
    if (mounted.current) setPrepared(challenge);
  };

  const fail = (
    error: unknown,
    provider: ReauthProvider,
    loginHint: string | null,
  ) =>
    setStateSafe({
      status: 'failed',
      failure: classifyReauthError(error, provider, loginHint),
    });

  const begin = async (provider: ReauthProvider) => {
    try {
      const reply = await fetch(AuthService.reauthBegin(provider), {
        silent: true,
      });
      const challenge: ReauthChallenge = {
        provider,
        nonce: reply.nonce,
        loginHint: reply.login_hint ?? null,
        expiresAtMs: Date.now() + CHALLENGE_LIFETIME_MS,
      };
      // Whatever was kept before, the server now holds this one.
      keep(challenge);
      return challenge;
    } catch (error) {
      if (isStaleModeError(error)) {
        onStaleMode?.();
      }
      throw error;
    }
  };

  const finish = async (challenge: ReauthChallenge, idToken: string) => {
    // Complete spends the server's challenge, whatever it answers.
    keep(null);
    try {
      const {reauth_token} = await fetch(
        AuthService.reauthComplete(challenge.provider, idToken),
        {silent: true},
      );
      setStateSafe({
        status: 'confirmed',
        provider: challenge.provider,
        reauthToken: reauth_token,
      });
    } catch (error) {
      fail(error, challenge.provider, challenge.loginHint);
    }
  };

  /**
   * A press that lands while ensurePrepared is drawing (the webapp draws on
   * focus or hover, so Tab then Enter can beat begin's reply) waits for that
   * draw rather than being dropped: it then finds the challenge kept, or
   * begins itself. Anything else in flight is the user's own and wins.
   * Awaited only then: otherwise nothing may be awaited before the provider
   * step, or a browser no longer sees it as part of the press (Apple's popup).
   */
  const silentDrawRunning = () => inFlight.current && silentDraw.current;
  const settleSilentDraw = async () => {
    while (silentDrawRunning()) {
      await silentDraw.current;
    }
  };

  /** Begin (unless a live challenge for `provider` is kept), sign in, complete. */
  const confirm = async (provider: ReauthProvider) => {
    if (!obtainIdToken) {
      return;
    }
    if (silentDrawRunning()) {
      await settleSilentDraw();
    }
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    silentFailed.current = false;
    const before = state;
    const kept = preparedRef.current;
    const ready = kept?.provider === provider && isLive(kept) ? kept : null;
    setStateSafe({status: 'busy', provider});
    try {
      let challenge: ReauthChallenge;
      if (ready) {
        challenge = ready;
      } else {
        try {
          challenge = await begin(provider);
        } catch (error) {
          fail(error, provider, null);
          return;
        }
      }
      let idToken: string | null;
      try {
        idToken = await obtainIdToken(provider, challenge);
      } catch (error) {
        // The challenge stays kept: the server has not spent it.
        fail(error, provider, challenge.loginHint);
        return;
      }
      if (!idToken) {
        // Dismissed: back to where the user was, saying nothing.
        setStateSafe(before.status === 'busy' ? {status: 'idle'} : before);
        return;
      }
      await finish(challenge, idToken);
    } finally {
      inFlight.current = false;
    }
  };

  /**
   * Complete with an ID token the platform obtained on its own (the webapp's
   * rendered Google button), against the kept challenge.
   */
  const complete = async (provider: ReauthProvider, idToken: string) => {
    if (inFlight.current) {
      return;
    }
    inFlight.current = true;
    silentFailed.current = false;
    setStateSafe({status: 'busy', provider});
    const challenge = preparedRef.current ?? {
      provider,
      nonce: '',
      loginHint: null,
      expiresAtMs: 0,
    };
    try {
      await finish(challenge, idToken);
    } finally {
      inFlight.current = false;
    }
  };

  /**
   * Draw a challenge for `provider` because the user asked (a press): the
   * failure is shown. An expiry notice stays up. Nothing to draw when a live
   * one for `provider` is kept (a silent draw it waited for landed first).
   */
  const prepare = async (provider: ReauthProvider) => {
    if (silentDrawRunning()) {
      await settleSilentDraw();
    }
    const kept = preparedRef.current;
    if (inFlight.current || (kept?.provider === provider && isLive(kept))) {
      return;
    }
    inFlight.current = true;
    silentFailed.current = false;
    const before = state;
    setStateSafe({status: 'busy', provider});
    try {
      await begin(provider);
      setStateSafe(before.status === 'expired' ? before : {status: 'idle'});
    } catch (error) {
      fail(error, provider, null);
    } finally {
      inFlight.current = false;
    }
  };

  /**
   * Draw a challenge for `provider` ahead of the press, silently, when the
   * app sees the user coming (the card focused or hovered): the webapp's
   * Google button bakes the nonce in when it renders. Draws nothing while a
   * live challenge is kept, for this provider or the other one (an Apple
   * retry must not lose its challenge), or after a silent failure until the
   * user tries something.
   */
  const ensurePrepared = async (provider: ReauthProvider) => {
    if (
      inFlight.current ||
      silentFailed.current ||
      isLive(preparedRef.current)
    ) {
      return;
    }
    inFlight.current = true;
    const draw = (async () => {
      try {
        await begin(provider);
      } catch {
        silentFailed.current = true;
      } finally {
        inFlight.current = false;
        silentDraw.current = null;
      }
    })();
    silentDraw.current = draw;
    await draw;
  };

  // A kept challenge past its local lifetime would only earn
  // challenge_expired: drop it.
  React.useEffect(() => {
    if (!prepared) {
      return;
    }
    const timer = setTimeout(
      () => {
        if (preparedRef.current === prepared) {
          keep(null);
        }
      },
      Math.max(0, prepared.expiresAtMs - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [prepared]);

  /** The email change refused the proof: back to the confirm step. */
  const expire = React.useCallback(() => {
    setState({status: 'expired'});
  }, []);

  /** Back to the confirm step, e.g. once the email change spent the proof. */
  const reset = React.useCallback(() => {
    setState({status: 'idle'});
  }, []);

  return {
    state,
    prepared,
    confirm,
    complete,
    prepare,
    ensurePrepared,
    expire,
    reset,
  };
};

export default useSocialReauth;
